/**
 * Loader hook for baked voxel cosmetic assets.
 *
 * - Promise-keyed by path so multiple `<VoxelDisplay path="..." />`
 *   instances mounted to the same SKU share one fetch.
 * - Validates `schema_version` on load — a stale runtime hitting a
 *   newer bake (or vice versa) surfaces as an error string instead of
 *   silently rendering wrong geometry.
 * - Cache is process-lifetime. There's no eviction; in practice the
 *   number of equipped/visible cosmetics per session is small (<200).
 *   If we hit growth-hook §12.2 (asset streaming), this is the spot
 *   to add an LRU + prefetch helper.
 */

import { useEffect, useState } from "react";

import { BAKE_SCHEMA_VERSION, type BakedVoxelAsset, type BakedVoxelAnimation } from "./types";

type CacheEntry<T> =
  | { kind: "loading"; promise: Promise<T> }
  | { kind: "ready"; value: T }
  | { kind: "error"; error: Error };

const ASSET_CACHE = new Map<string, CacheEntry<BakedVoxelAsset>>();
const ANIM_CACHE = new Map<string, CacheEntry<BakedVoxelAnimation>>();

export interface UseVoxelAssetResult {
  data: BakedVoxelAsset | null;
  loading: boolean;
  error: Error | null;
}

export interface UseVoxelAnimationResult {
  data: BakedVoxelAnimation | null;
  loading: boolean;
  error: Error | null;
}

export interface UseVoxelAssetsResult {
  /** Loaded assets in input-path order. Entries are `null` while loading. */
  data: ReadonlyArray<BakedVoxelAsset | null>;
  /** True until every path has resolved (or errored). */
  loading: boolean;
  /** First error encountered, if any. */
  error: Error | null;
}

/**
 * Load + cache a baked-VOX static asset. Returns `{ data, loading, error }`.
 *
 * Pass `path=null` (or undefined) to skip loading — useful when the
 * SKU isn't yet selected.
 */
export function useVoxelAsset(path: string | null | undefined): UseVoxelAssetResult {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) return;
    const cached = ASSET_CACHE.get(path);
    if (cached) return; // either ready or loading; the active fetch resolves either way

    const promise = fetchAsset(path);
    ASSET_CACHE.set(path, { kind: "loading", promise });
    promise
      .then((value) => {
        ASSET_CACHE.set(path, { kind: "ready", value });
        setTick((t) => t + 1);
      })
      .catch((err) => {
        ASSET_CACHE.set(path, { kind: "error", error: toError(err) });
        setTick((t) => t + 1);
      });
  }, [path]);

  if (!path) return { data: null, loading: false, error: null };
  const entry = ASSET_CACHE.get(path);
  if (!entry) return { data: null, loading: true, error: null };
  if (entry.kind === "ready") return { data: entry.value, loading: false, error: null };
  if (entry.kind === "error") return { data: null, loading: false, error: entry.error };
  // re-render once the loading promise resolves
  void tick;
  return { data: null, loading: true, error: null };
}

export function useVoxelAnimation(path: string | null | undefined): UseVoxelAnimationResult {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) return;
    const cached = ANIM_CACHE.get(path);
    if (cached) return;

    const promise = fetchAnimation(path);
    ANIM_CACHE.set(path, { kind: "loading", promise });
    promise
      .then((value) => {
        ANIM_CACHE.set(path, { kind: "ready", value });
        setTick((t) => t + 1);
      })
      .catch((err) => {
        ANIM_CACHE.set(path, { kind: "error", error: toError(err) });
        setTick((t) => t + 1);
      });
  }, [path]);

  if (!path) return { data: null, loading: false, error: null };
  const entry = ANIM_CACHE.get(path);
  if (!entry) return { data: null, loading: true, error: null };
  if (entry.kind === "ready") return { data: entry.value, loading: false, error: null };
  if (entry.kind === "error") return { data: null, loading: false, error: entry.error };
  void tick;
  return { data: null, loading: true, error: null };
}

/**
 * Bulk-load N voxel assets in parallel. Used by `<VoxelAnimation>` to
 * load every frame of a manifest without violating React's rules of
 * hooks (one hook call regardless of frame count). Each path threads
 * through the same path-keyed cache as `useVoxelAsset`.
 *
 * The path array is joined to a single dependency string so adding /
 * removing frames triggers a re-evaluation, but element-wise mutation
 * of an unchanged paths array does not.
 */
export function useVoxelAssets(
  paths: ReadonlyArray<string> | null | undefined,
): UseVoxelAssetsResult {
  const [tick, setTick] = useState(0);
  const key = paths ? paths.join("|") : "";

  useEffect(() => {
    if (!paths || paths.length === 0) return;
    let cancelled = false;
    const pending: Promise<unknown>[] = [];
    for (const p of paths) {
      const cached = ASSET_CACHE.get(p);
      if (cached) continue;
      const promise = fetchAsset(p);
      ASSET_CACHE.set(p, { kind: "loading", promise });
      pending.push(
        promise
          .then((value) => {
            ASSET_CACHE.set(p, { kind: "ready", value });
          })
          .catch((err) => {
            ASSET_CACHE.set(p, { kind: "error", error: toError(err) });
          }),
      );
    }
    if (pending.length > 0) {
      Promise.allSettled(pending).then(() => {
        if (!cancelled) setTick((t) => t + 1);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!paths || paths.length === 0) {
    return { data: [], loading: false, error: null };
  }
  const data: (BakedVoxelAsset | null)[] = [];
  let loading = false;
  let firstError: Error | null = null;
  for (const p of paths) {
    const entry = ASSET_CACHE.get(p);
    if (!entry) {
      data.push(null);
      loading = true;
      continue;
    }
    if (entry.kind === "ready") {
      data.push(entry.value);
    } else if (entry.kind === "loading") {
      data.push(null);
      loading = true;
    } else {
      data.push(null);
      if (!firstError) firstError = entry.error;
    }
  }
  void tick;
  return { data, loading, error: firstError };
}

// ── Internals ──────────────────────────────────────────────────────────────

async function fetchAsset(path: string): Promise<BakedVoxelAsset> {
  const res = await fetch(path);
  if (!res.ok) {
    throw new Error(`failed to load voxel asset ${path}: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as BakedVoxelAsset;
  validateAsset(path, json);
  return json;
}

async function fetchAnimation(path: string): Promise<BakedVoxelAnimation> {
  const res = await fetch(path);
  if (!res.ok) {
    throw new Error(`failed to load voxel animation ${path}: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as BakedVoxelAnimation;
  validateAnimation(path, json);
  return json;
}

function validateAsset(path: string, asset: BakedVoxelAsset): void {
  if (!asset || typeof asset !== "object") {
    throw new Error(`voxel asset ${path} is not an object`);
  }
  if (asset.schema_version !== BAKE_SCHEMA_VERSION) {
    throw new Error(
      `voxel asset ${path} schema_version=${asset.schema_version} ≠ expected ${BAKE_SCHEMA_VERSION}; ` +
        `re-run \`pnpm vox:bake\` after pulling main`,
    );
  }
  if (!Array.isArray(asset.voxels)) {
    throw new Error(`voxel asset ${path} missing voxels array`);
  }
  if (!asset.slot_palette || typeof asset.slot_palette !== "object") {
    throw new Error(`voxel asset ${path} missing slot_palette`);
  }
}

function validateAnimation(path: string, anim: BakedVoxelAnimation): void {
  if (!anim || typeof anim !== "object") {
    throw new Error(`voxel animation ${path} is not an object`);
  }
  if (anim.schema_version !== BAKE_SCHEMA_VERSION) {
    throw new Error(
      `voxel animation ${path} schema_version=${anim.schema_version} ≠ expected ${BAKE_SCHEMA_VERSION}`,
    );
  }
  if (!Array.isArray(anim.frames) || anim.frames.length === 0) {
    throw new Error(`voxel animation ${path} missing non-empty frames array`);
  }
  if (typeof anim.fps !== "number" || anim.fps <= 0) {
    throw new Error(`voxel animation ${path} missing positive fps`);
  }
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(String(err));
}

// ── Testing-only helpers ──────────────────────────────────────────────────

/** Clear the asset + animation caches. Tests only. */
export function _resetVoxelAssetCache(): void {
  ASSET_CACHE.clear();
  ANIM_CACHE.clear();
}

/** Pre-seed the cache with a known asset. Stories use this when
 *  serving a synthetic fixture instead of a real network fetch. */
export function _seedVoxelAssetCache(path: string, asset: BakedVoxelAsset): void {
  ASSET_CACHE.set(path, { kind: "ready", value: asset });
}

export function _seedVoxelAnimationCache(path: string, anim: BakedVoxelAnimation): void {
  ANIM_CACHE.set(path, { kind: "ready", value: anim });
}
